import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, MenuItem, Select, InputLabel, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert } from '@mui/material';
import api from '../api/axios';

interface TenantOption {
  id: number;
  name: string;
}

interface AdminUser {
  id: number;
  email: string;
  is_active: boolean;
  role: { id: number; name: string };
}

const AdminsPage: React.FC = () => {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>('');
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [adminRoleId, setAdminRoleId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState({ email: '', password: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchTenants();
    fetchAdminRoleId();
  }, []);

  useEffect(() => {
    if (selectedTenant) fetchAdmins(selectedTenant);
  }, [selectedTenant]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenants/', { headers: { Authorization: `Bearer ${token}` } });
      setTenants(res.data as TenantOption[]);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch tenants', severity: 'error' });
    }
  };

  const fetchAdminRoleId = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/roles/', { headers: { Authorization: `Bearer ${token}` } });
      const adminRole = (res.data as any[]).find((r: any) => r.name === 'admin');
      setAdminRoleId(adminRole?.id || null);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch admin role', severity: 'error' });
    }
  };

  const fetchAdmins = async (tenantId: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/api/v1/admin/admin-users/?tenant_id=${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAdmins(res.data as AdminUser[]);
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch admins', severity: 'error' });
    }
  };

  const handleOpenDialog = (user?: AdminUser) => {
    setEditUser(user || null);
    setForm({ email: user?.email || '', password: '' });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditUser(null);
    setForm({ email: '', password: '' });
  };

  const handleSave = async () => {
    if (!selectedTenant || !adminRoleId) return;
    try {
      const token = localStorage.getItem('token');
      if (editUser) {
        // Edit existing admin
        await api.put(`/api/v1/admin/admin-users/${editUser.id}/`, { email: form.email, password: form.password || undefined }, { headers: { Authorization: `Bearer ${token}` } });
        setSnackbar({ open: true, message: 'Admin updated', severity: 'success' });
      } else {
        // Create new admin
        await api.post('/api/v1/admin/admin-users/', { email: form.email, password: form.password, tenant_id: selectedTenant, role_id: adminRoleId }, { headers: { Authorization: `Bearer ${token}` } });
        setSnackbar({ open: true, message: 'Admin created', severity: 'success' });
      }
      fetchAdmins(selectedTenant);
      handleCloseDialog();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save admin', severity: 'error' });
    }
  };

  const handleDisable = async (user: AdminUser) => {
    if (!window.confirm('Disable this admin?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/v1/admin/admin-users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: 'Admin disabled', severity: 'success' });
      fetchAdmins(selectedTenant as number);
    } catch {
      setSnackbar({ open: true, message: 'Failed to disable admin', severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Admins</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Tenant</InputLabel>
          <Select
            value={selectedTenant}
            label="Select Tenant"
            onChange={e => setSelectedTenant(Number(e.target.value))}
          >
            {tenants.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => handleOpenDialog()} disabled={!selectedTenant || !adminRoleId}>Create Admin</Button>
      </Paper>
      {selectedTenant && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map(admin => (
                <TableRow key={admin.id}>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>{admin.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleOpenDialog(admin)}>Edit</Button>
                    <Button size="small" color="error" onClick={() => handleDisable(admin)}>Disable</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{editUser ? 'Edit Admin' : 'Create Admin'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            fullWidth
            margin="normal"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            helperText={editUser ? 'Leave blank to keep current password' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminsPage; 